'use client';

import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import clsx from 'clsx';
import { FC, useState } from 'react';

import { CallToAction } from '@/app/_componentsv2/call-to-action';
import { VideoPlayer } from '@/app/_componentsv2/media/video-player';

import {
  content,
  defaultInnerWrapper,
  defaultList,
  defaultListHeading,
  defaultListHeadingDescription,
  defaultListItem,
  defaultListItemBullet,
  defaultListItemDescription,
  defaultListItemTitle,
  defaultOuterWrapper,
  details,
  detailsImage,
  detailsVideo,
  images,
  innerWrapper,
  list,
  listItem,
  listItemDescription,
  listItemTitle,
  outerWrapper,
  selectedListItem,
  title,
  LightWithCtaListItem,
  lightWithCtaListItemDescription,
  LightWithCtaListItemBullet,
  callToAction,
} from './index.css';

/**
 * Props for `List`.
 */
export type ListProps = SliceComponentProps<Content.ListSlice>;

/**
 * Component for "List" Slices.
 */
const List: FC<ListProps> = (props) => {
  const { slice } = props;

  if (slice.variation === 'videos') {
    return <VideosList sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'default') {
    return <DefaultList sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'images') {
    return <ImageList sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'lightWithCta') {
    return <LightWithCtaList sliceType={slice.slice_type} slice={slice} />;
  }
};

function ImageList({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.ListSliceImages;
}) {
  const [selected, setSelected] = useState(0);

  const selectedImage = slice.primary.items[selected];

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={clsx(outerWrapper, images)}
    >
      <section className={innerWrapper}>
        <PrismicRichText
          components={{
            paragraph: ({ children }) => <h2 className={title}>{children}</h2>,
          }}
          field={slice.primary.title}
        />
        <section className={content}>
          <section className={details}>
            <PrismicNextImage
              fallbackAlt=""
              className={detailsImage}
              field={selectedImage.image}
            />
          </section>
          <section className={list}>
            {slice.primary.items.map((item, index) => (
              <button
                key={index}
                className={clsx(listItem, {
                  [selectedListItem]: index === selected,
                })}
                disabled={index === selected}
                onClick={() => setSelected(index)}
              >
                <PrismicRichText
                  components={{
                    paragraph: ({ children }) => (
                      <span className={listItemTitle}>{children}</span>
                    ),
                  }}
                  field={item.title}
                />
                <PrismicRichText
                  components={{
                    paragraph: ({ children }) => (
                      <span className={listItemDescription}>{children}</span>
                    ),
                  }}
                  field={item.description}
                />
              </button>
            ))}
          </section>
        </section>
      </section>
    </section>
  );
}

function DefaultList({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.ListSliceDefault;
}) {
  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={defaultOuterWrapper}
    >
      <section className={defaultInnerWrapper}>
        <section className={defaultListHeading}>
          <PrismicRichText
            components={{
              paragraph: ({ children }) => <h2>{children}</h2>,
            }}
            field={slice.primary.title}
          />
          <PrismicRichText
            components={{
              paragraph: ({ children }) => (
                <p className={defaultListHeadingDescription}>{children}</p>
              ),
            }}
            field={slice.primary.description}
          />
        </section>
        <section className={defaultList}>
          {slice.primary.items.map((item, index) => (
            <section className={defaultListItem} key={index}>
              <span className={defaultListItemBullet}>
                {`${index + 1}`.padStart(2, '0')}
              </span>
              <PrismicRichText
                components={{
                  paragraph: ({ children }) => (
                    <p className={defaultListItemTitle}>{children}</p>
                  ),
                }}
                field={item.title}
              />
              <PrismicRichText
                components={{
                  paragraph: ({ children }) => (
                    <p className={defaultListItemDescription}>{children}</p>
                  ),
                }}
                field={item.description}
              />
            </section>
          ))}
        </section>
      </section>
    </section>
  );
}

function VideosList({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.ListSliceVideos;
}) {
  const [selected, setSelected] = useState(0);

  const selectedVideo = slice.primary.items[selected];

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={outerWrapper}
    >
      <section className={innerWrapper}>
        <PrismicRichText
          components={{
            paragraph: ({ children }) => <h2 className={title}>{children}</h2>,
          }}
          field={slice.primary.title}
        />
        <section className={content}>
          <section className={list}>
            {slice.primary.items.map((item, index) => (
              <button
                key={index}
                className={clsx(listItem, {
                  [selectedListItem]: index === selected,
                })}
                disabled={index === selected}
                onClick={() => setSelected(index)}
              >
                <PrismicRichText
                  components={{
                    paragraph: ({ children }) => (
                      <span className={listItemTitle}>{children}</span>
                    ),
                  }}
                  field={item.title}
                />
                <PrismicRichText
                  components={{
                    paragraph: ({ children }) => (
                      <span className={listItemDescription}>{children}</span>
                    ),
                  }}
                  field={item.description}
                />
              </button>
            ))}
          </section>
          <section className={details}>
            <VideoPlayer
              classNames={{ wrapper: detailsVideo }}
              field={selectedVideo.video}
              image={selectedVideo.video_thumbnail}
            />
          </section>
        </section>
      </section>
    </section>
  );
}

function LightWithCtaList({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.ListSliceLightWithCta;
}) {
  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={defaultOuterWrapper}
    >
      <section className={defaultInnerWrapper}>
        <section className={defaultListHeading}>
          <PrismicRichText
            components={{
              paragraph: ({ children }) => <h2>{children}</h2>,
            }}
            field={slice.primary.title}
          />
        </section>
        <section className={defaultList}>
          {slice.primary.items.map((item, index) => (
            <section className={LightWithCtaListItem} key={index}>
              <span className={LightWithCtaListItemBullet}>
                {`${index + 1}`.padStart(2, '')}
              </span>
              <PrismicRichText
                components={{
                  paragraph: ({ children }) => (
                    <p className={lightWithCtaListItemDescription}>
                      {children}
                    </p>
                  ),
                }}
                field={item.description}
              />
            </section>
          ))}
        </section>
        <CallToAction
          type="prismic"
          className={callToAction}
          field={slice.primary.call_to_action}
        />
      </section>
    </section>
  );
}

export default List;
